import type { PublicacaoDto } from "@base/contracts";
import { CircleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button, Modal } from "@base/ui/renke";
import {
  IconePlataforma,
  ROTULO_PLATAFORMA,
} from "../../../shared/branding/icone-plataforma";

type Props = {
  readonly falhas: readonly PublicacaoDto[] | null;
  readonly onFechar: () => void;
  readonly onReprocessar?: (ids: readonly string[]) => void;
  readonly reprocessando?: boolean;
};

/**
 * Mostra os erros de uma postagem (uma ou varias redes).
 */
export function ModalDetalheFalhaPublicacao({
  falhas,
  onFechar,
  onReprocessar,
  reprocessando = false,
}: Props) {
  const lista = falhas ?? [];
  const aberto = lista.length > 0;
  const primeira = lista[0] ?? null;
  const tituloPost =
    primeira?.nome.trim() || primeira?.legenda.trim() || "Publicação sem nome";
  const horario = primeira?.agendarPara
    ? new Date(primeira.agendarPara).toLocaleString("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Modal
      aberto={aberto}
      titulo={lista.length > 1 ? "Erros desta postagem" : "Detalhe da falha"}
      descricao={tituloPost}
      onFechar={onFechar}
      className="max-w-lg"
      rodape={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {lista.length === 1 && onReprocessar ? (
            <Button
              type="button"
              variante="secondary"
              tamanho="sm"
              disabled={reprocessando}
              onClick={() => onReprocessar([lista[0]!.id])}
            >
              Reprocessar
            </Button>
          ) : null}
          {lista.length > 1 && onReprocessar ? (
            <Button
              type="button"
              variante="secondary"
              tamanho="sm"
              disabled={reprocessando}
              onClick={() => onReprocessar(lista.map((p) => p.id))}
            >
              Reprocessar todas
            </Button>
          ) : null}
          <Button
            type="button"
            variante="secondary"
            tamanho="sm"
            onClick={onFechar}
          >
            Fechar
          </Button>
        </div>
      }
    >
      {lista.length > 0 ? (
        <div className="mt-4 space-y-6">
          {horario ? (
            <p className="text-sm capitalize text-muted-foreground">{horario}</p>
          ) : null}

          <ul className="space-y-4">
            {lista.map((publicacao) => {
              const motivo =
                publicacao.motivoFalha?.trim() ||
                "Sem detalhe gravado nesta falha.";
              return (
                <li key={publicacao.id} className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge variant="destructive" className="gap-1">
                      <CircleAlert className="size-3" aria-hidden />
                      Falhou
                    </Badge>
                    <Badge
                      variant="outline"
                      className="inline-flex items-center gap-1.5"
                    >
                      <IconePlataforma
                        plataforma={publicacao.plataforma}
                        tom="falha"
                        className="size-3.5"
                      />
                      {ROTULO_PLATAFORMA[publicacao.plataforma]}
                    </Badge>
                    <Badge variant="secondary">
                      {publicacao.tentativas} tentativa
                      {publicacao.tentativas === 1 ? "" : "s"}
                    </Badge>
                    {lista.length > 1 && onReprocessar ? (
                      <Button
                        type="button"
                        variante="secondary"
                        tamanho="sm"
                        disabled={reprocessando}
                        aria-label={`Reprocessar ${ROTULO_PLATAFORMA[publicacao.plataforma]}`}
                        onClick={() => onReprocessar([publicacao.id])}
                      >
                        Reprocessar
                      </Button>
                    ) : null}
                  </div>
                  <pre
                    role="alert"
                    className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 font-mono text-xs leading-relaxed text-destructive"
                  >
                    {motivo}
                  </pre>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </Modal>
  );
}
